import { useEffect, useCallback } from "react";

export interface KeyboardShortcut {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  preventDefault?: boolean;
  action: () => void;
  description: string;
}

export function useKeyboardShortcuts(
  shortcuts: KeyboardShortcut[],
  enabled = true,
) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      for (const shortcut of shortcuts) {
        const isMatch =
          event.key.toLowerCase() === shortcut.key.toLowerCase() &&
          !!event.metaKey === (shortcut.metaKey ?? false) &&
          !!event.ctrlKey === (shortcut.ctrlKey ?? false) &&
          !!event.shiftKey === (shortcut.shiftKey ?? false) &&
          !!event.altKey === (shortcut.altKey ?? false);

        if (isMatch) {
          if (shortcut.preventDefault !== false) {
            event.preventDefault();
          }
          shortcut.action();
          break;
        }
      }
    },
    [shortcuts, enabled],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
}

// Common shortcuts for the search system
export const createSearchShortcuts = (
  openSearch: () => void,
): KeyboardShortcut[] => [
  {
    key: "k",
    metaKey: true,
    action: openSearch,
    description: "Open search palette",
  },
  {
    key: "k",
    ctrlKey: true,
    action: openSearch,
    description: "Open search palette (Windows/Linux)",
  },
];

// Accessibility helper for screen readers
export const announceToScreenReader = (message: string) => {
  const announcement = document.createElement("div");
  announcement.setAttribute("aria-live", "polite");
  announcement.setAttribute("aria-atomic", "true");
  announcement.setAttribute(
    "style",
    "position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;",
  );
  announcement.textContent = message;

  document.body.appendChild(announcement);

  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};
